import React, { useContext, useEffect, useRef, useState } from "react";
import { ProgressBar } from "@adeccoux/tag-ds";
import { getNoCacheCall, postCall } from "../../util/resources";
import { useLocation, useNavigate } from "react-router-dom";
import {
    TEST_COMPLETE,
    TEST_QUESTIONS,
    UPDATE_INTERSTITIAL_STATUS,
} from "../../util/constants";
import {
    CommonLoader,
    CommonPopup,
    QuesLoader,
    SessionExpireCommonPopup,
    useScreenTransition,
    useTimer,
    redirectToAssessmentSummary,
    useTimerValues,
} from "../../util/common";
import Interstitial from "./Interstitial";
import SesHeader from "../header";
import { BaseContext } from "../../App";
import WarmupComplete from "../instructions/warmupComplete";

const Question = () => {
    const location = useLocation();
    const [startMainTest, setStartMainTest] = useState(false);
    const [sessionStatus, setSessionStatus] = useState(false);
    const [test, setTest] = useState();
    const [selectedAns, setSelectedAns] = useState(-1);
    const [showInterstitialScreen, setInterstitialScreen] = useState(false);
    const { isWarmupDoneShown, setWarmupDoneShown } = useContext(BaseContext);
    const { phase, loading, startTransition, done } = useScreenTransition();
    const phrases = location.state.user.siteConfig.phrases;
    const navigate = useNavigate();
    const quesNo = useRef(-1);
    const keyMappings = useRef({});
    const answerData = useRef(-1);
    const previousBtn = useRef(true);
    const prevExampleAns = useRef(-1);
    const attachKeyListenRef = useRef();
    const stepRef = useRef();
    const interstitialSeen = useRef(location.state.test.interstitialSeen);
    const interstitialQuestionNumber = Math.floor(
        location.state.test.questionsCount *
        location.state.test.testCompletionPercent
    );
    const interstitialPage = Math.ceil(
        interstitialQuestionNumber / location.state.test.qperPage
    );
    const header = useRef({
        "x-xpert-session": location.state.user.sessionId,
        "Content-Type": "application/json",
    });
    const isWarmUp = useRef(
        location.state.warmUp ? location.state.warmUp : false
    );
    const timerStarted = useRef(false);
    const checkTestType = ["3", "35"];
    const { testType } = location.state.test;
    const [popupData, setpopupData] = useState({
        isOpen: false,
        popupHeader: "",
        popupBody: "",
        popupFooter: "",
    });
    const { min, sec, totalSecLeft, startTimer, stopTimer } = useTimer();
    const { timerOverallAssessment } = useTimerValues();

    useEffect(() => {
        if (isWarmUp.current) {
            quesNo.current = 0;
        }
        getQuestion();
    }, [startMainTest]);

    useEffect(() => {
        if (test) {
            attachKeyListenRef.current?.focus({
                preventScroll: true,
            });
        }
    }, [test]);

    useEffect(() => {
        if (testType.toString() === "35") {
            if (totalSecLeft === 0) {
                setSessionStatus(true);
            }
            if (test && location.state.test.bShowTimer) {
                stepRef?.current?.children[1]?.children[0]?.setAttribute(
                    "data-secLeft",
                    `${min}:${sec}`
                );
            }
        }
    }, [totalSecLeft]);

    const getQuestion = async () => {
        if (isWarmUp.current) {
            setTest(location.state.test.testExample[quesNo.current]);
            let key;
            keyMappings.current = {};
            location.state.test.testExample[
                quesNo.current
            ].questions[0].answers.map((val) => {
                key = `Digit${val.position + 1}`;
                keyMappings.current = {
                    ...keyMappings.current,
                    [key]: val.position,
                };
            });
            quesNo.current += 1;
        } else if (
            location.state.test.interstitial &&
            interstitialSeen.current === "N" &&
            quesNo.current == interstitialPage + 1
        ) {
            interstitialSeen.current = "Y";
            setInterstitialScreen(true);
            const response = await postCall(
                UPDATE_INTERSTITIAL_STATUS + location.state.test.id,
                { test: location.state.test.id },
                header.current
            );
        } else {
            try {
                const response = await getNoCacheCall(
                    TEST_QUESTIONS +
                    location.state.test.id +
                    "/" +
                    quesNo.current,
                    header.current
                );
                setTest(response.data[0]);
                setSelectedAns(
                    response.data[0].questions[0].userAnswer
                        ? response.data[0].questions[0].userAnswer.value
                        : -1
                );
                quesNo.current = response.data[0].startNumber;
                let key;
                keyMappings.current = {};
                response.data[0].questions[0].answers.map((val) => {
                    key = `Digit${val.position + 1}`;
                    keyMappings.current = {
                        ...keyMappings.current,
                        [key]: val.position,
                    };
                });
                if (testType.toString() === "35" && !timerStarted.current) {
                    startTimer(
                        location.state.test.timeLimit -
                        location.state.test.duration
                    );
                    timerStarted.current = true;
                } else {
                    startTimer();
                }
            } catch (error) {
                console.log();
            }
        }
    };

    const handleNextQs = (e, click = false) => {
        // Prevent handling while a question is being fetched/transitioned.
        if (loading || !test) return;
        answerData.current = e.target.value;
        setSelectedAns(e.target.value);
        if (
            click &&
            !test.lastPage &&
            !checkTestType.includes(testType.toString())
        ) {
            nextNavigate();
        }
    };

    const detectKeyDown = (e) => {
        e.preventDefault();
        if (!loading) {
            if (e.code in keyMappings.current) {
                setSelectedAns(keyMappings.current[e.code]);
                answerData.current = keyMappings.current[e.code];
                if (
                    !checkTestType.includes(testType.toString()) &&
                    !test.lastPage &&
                    !showInterstitialScreen
                ) {
                    nextNavigate();
                }
            }
            if (
                !test.lastPage &&
                selectedAns !== -1 &&
                !showInterstitialScreen &&
                e.code === "Space"
            ) {
                nextNavigate();
            }
        }
    };

    const nextNavigate = async (previous = false, end) => {
        startTransition(async () => {
            if (isWarmUp.current) {
                if (test.lastPage && previous !== true) {
                    handleExampleStart();
                } else {
                    if (previous === true) {
                        previousBtn.current = false;
                        quesNo.current -= 2;
                        setSelectedAns(prevExampleAns.current);
                    } else {
                        prevExampleAns.current = answerData.current;
                        previousBtn.current = true;
                        setSelectedAns(-1);
                    }
                    await getQuestion();
                }
            } else {
                const ansObj = {
                    answers: [],
                    structure: null,
                    value: answerData.current,
                };
                let quesObj = test.questions;
                quesObj[0].userAnswer = ansObj;
                const elapsedTime = stopTimer();
                const otherData = {
                    duration: location.state.test.duration + elapsedTime,
                };
                try {
                    let response;
                    if ((test.lastPage && !previous) || end === "timeEnded") {
                        response = await postCall(
                            TEST_COMPLETE + location.state.test.id,
                            { ...otherData, questions: quesObj },

                            header.current
                        );
                        redirectToAssessmentSummary(location, navigate);
                    } else {
                        response = await postCall(
                            TEST_QUESTIONS + location.state.test.id,
                            { ...otherData, questions: quesObj },
                            header.current
                        );
                    }
                    if (response.data.status === true) {
                        if (test.lastPage) {
                            if (!previous) {
                                redirectToAssessmentSummary(location, navigate);
                            } else {
                                previousBtn.current = false;
                                setSelectedAns(-1);
                                answerData.current = -1;
                                quesNo.current -= 1;
                                await getQuestion();
                            }
                        } else {
                            if (!previous) {
                                previousBtn.current = true;
                                setSelectedAns(-1);
                                answerData.current = -1;
                                quesNo.current += 1;
                                await getQuestion();
                            } else {
                                previousBtn.current = false;
                                setSelectedAns(-1);
                                answerData.current = -1;
                                quesNo.current -= 1;
                                await getQuestion();
                            }
                        }
                    }
                } catch (error) {
                    console.log(error);
                    setQuesError(true);
                }
            }
            done();
        }, !isWarmUp.current);
    };

    const exitTest = () => {
        if (isWarmUp.current) {
            navigate("/instructions", {
                state: { ...location.state, warmUp: false },
            });
        } else {
            setpopupData((prev) => {
                prev = {
                    ...prev,
                    isOpen: true,
                    popupHeader: location.state.test.name,
                    popupBody: phrases?.["1002189"],
                    popupFooter: phrases?.["14392"],
                };
                return prev;
            });
        }
    };

    const closePopUp = () => {
        setpopupData((prev) => {
            prev = { ...prev, isOpen: false };
            return prev;
        });
        stopTimer();
        navigate("/");
    };

    const approveAction = async () => {
        if (isWarmUp.current) {
            navigate("/instructions", {
                state: { ...location.state },
            });
        } else {
            const ansObj = {
                answers: [],
                structure: null,
                value: answerData.current,
            };
            let quesObj = test.questions;
            quesObj[0].userAnswer = ansObj;
            const elapsedTime = stopTimer();
            const otherData = {
                duration: location.state.test.duration + elapsedTime,
            };
            await postCall(
                TEST_COMPLETE + location.state.test.id,
                { ...otherData, questions: quesObj },
                header.current
            );
            redirectToAssessmentSummary(location, navigate);
        }
    };

    const interstitialContinue = () => {
        startTransition(async () => {
            setInterstitialScreen(false);
            await getQuestion();
            done();                      // release the transition/loader
        });
    };


    const handleExampleStart = () => {
        setWarmupDoneShown(true);
    };

    const handleMainTestStart = () => {
        isWarmUp.current = false;
        quesNo.current = -1;
        answerData.current = -1;
        setWarmupDoneShown(false);
        setStartMainTest(true);
    };

    const sessionApproveAction = async () => {
        setSessionStatus(false);
        nextNavigate(false, "timeEnded");
    };

    if (!test) {
        return <CommonLoader />;
    }

    if (isWarmupDoneShown) {
        return (
            <WarmupComplete
                backtoInst={exitTest}
                handleMainTestStart={handleMainTestStart}
            />
        );
    }

    return (
        <div
            className={
                showInterstitialScreen
                    ? "full-width"
                    : "full-width container bg01 roundness_1 "
            }
            ref={attachKeyListenRef}
            tabIndex="-1"
            onKeyDown={detectKeyDown}
            onBlur={(e) =>
                e.target.focus({
                    preventScroll: true,
                })
            }
            style={{ outline: "none" }}
        >
            <div className="g-container_12 flex_col align-items_start g1 g2_mobile p2 py3_tablet py5_tablet">
                <SessionExpireCommonPopup
                    sessionStatus={sessionStatus}
                    sessionApproveAction={sessionApproveAction}
                />
                {showInterstitialScreen ? (
                    <Interstitial
                        phrases={phrases}
                        interstitialContinue={interstitialContinue}
                    />
                ) : (
                    <>
                        <div className="g-item_12 flex_col align-items_center justify-content_center align-self-stretch g1">
                            <SesHeader
                                clientCobranding={location.state.cobrandingLink}
                                isWarmUp={isWarmUp.current}
                            />
                        </div>
                        <CommonPopup
                            popupData={popupData}
                            closeAction={closePopUp}
                            approveAction={approveAction}
                            setterFun={setpopupData}
                        />
                        <div
                            ref={stepRef}
                            className="g-item_12 g1 flex_row align-items_start align-self-stretch top-progress-container"
                        >
                            <ProgressBar
                                className="full-width"
                                helper={{
                                    left: isWarmUp.current
                                        ? test.startNumber +
                                        "/" +
                                        location.state.test.testExample
                                            .length +
                                        " " +
                                        phrases?.["13106"]
                                        : test.startNumber +
                                        "/" +
                                        location.state.test.questionsCount +
                                        " " +
                                        phrases?.["13106"],
                                }}
                                showSteps
                                step={test?.startNumber}
                                steps={
                                    isWarmUp.current
                                        ? location.state.test.testExample.length
                                        : location.state.test.questionsCount
                                }
                                title={phrases?.["14798"]}
                            />
                            {testType.toString() === "35" &&
                                !isWarmUp.current && (
                                    <ProgressBar
                                        circular
                                        step={totalSecLeft}
                                        steps={location.state.test.timeLimit}
                                        circularColorClass={
                                            timerOverallAssessment !==
                                                undefined &&
                                                totalSecLeft <=
                                                timerOverallAssessment
                                                ? "progress-bar-color"
                                                : ""
                                        }
                                    />
                                )}
                        </div>
                        <div className="g-item_12 g2 flex_col align-items_start align-self-stretch ">
                            <h3 className="align-self-stretch text-01">
                                {isWarmUp.current
                                    ? location.state.test?.testExampleName
                                    : location.state.test.name}
                            </h3>
                        </div>
                        <div className="g-item_12 position_relative g2 flex flex_col_tablet align-self-stretch justify-content_center">
                            <QuesLoader show={phase === "showing-loader"} />
                            <div
                                className={`g-item_6 flex_col g2 align-items_start shown ${phase}`}
                            >
                                <p className="small-title m0">
                                    {phrases?.["13106"]}
                                </p>
                                <div className="full-width full-height flex_col p4 g2 align-self-stretch roundness_1 question-brdr">
                                    <div
                                        className="base_bold align-self-stretch questionText"
                                        dangerouslySetInnerHTML={{
                                            __html: test?.questions[0]
                                                ?.questionText,
                                        }}
                                    ></div>
                                </div>
                            </div>
                            <div
                                className={`g-item_6 flex_col g2 align-items_start shown ${phase}`}
                            >
                                <p className="small-title m0">
                                    {phrases?.["15138"]}
                                </p>
                                <div className="full-width full-height flex_col p1 justify-content_center align-items_center g2 align-self-stretch roundness_1 question-brdr">
                                    <div className="full-width flex_col align-items_start  align-self-stretch">
                                        {test?.questions[0]?.answers.map(
                                            (val, ind) => (
                                                <div
                                                    key={test.startNumber + ind}
                                                    className="radio-container"
                                                >
                                                    <input
                                                        id={`option-${ind}`}
                                                        type="radio"
                                                        value={val.position}
                                                        checked={
                                                            selectedAns ==
                                                            val.position
                                                        }
                                                        disabled={loading}
                                                        onClick={(e) =>
                                                            handleNextQs(
                                                                e,
                                                                true
                                                            )
                                                        }
                                                    />
                                                    <label
                                                        dangerouslySetInnerHTML={{
                                                            __html: val.content,
                                                        }}
                                                        htmlFor={`option-${ind}`}
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="g-item_12 flex_col align-items_start align-self-stretch g2">
                            <div className="flex align-self-stretch align-items_center justify-content_center full-width">
                                <div className=" full-width"></div>
                            </div>
                            <div className="full-width flex flex_col_mobile g2_mobile align-items_start_mobile align-items_center  align-self-stretch justify-content_between">
                                {(!test.lastPage || isWarmUp.current) &&
                                    testType != "12" && (
                                        <button
                                            className="button-secondary text_uppercase full-width_mobile"
                                            onClick={exitTest}
                                            disabled={loading}
                                        >
                                            {isWarmUp.current
                                                ? phrases?.["1000229"]
                                                : phrases?.["1002190"]}
                                        </button>
                                    )}
                                <div className="full-width g1 flex flex_col_mobile g2_mobile align-items_start_mobile align-items_center align-self-stretch justify-content_end">
                                    <div className="justify-content_between flex g1 full-width_mobile">
                                        {!checkTestType.includes(
                                            testType.toString()
                                        ) &&
                                            !location.state.test
                                                .previosNavegation &&
                                            test?.startNumber !== 1 && (
                                                <button
                                                    className="button-primary text_uppercase full-width_mobile"
                                                    disabled={
                                                        !previousBtn.current ||
                                                        loading
                                                    }
                                                    onClick={() => {
                                                        nextNavigate(true);
                                                    }}
                                                >
                                                    <span className="material-icons">
                                                        arrow_back
                                                    </span>
                                                    {phrases?.["410"]}
                                                </button>
                                            )}
                                        {(!checkTestType.includes(
                                            testType.toString()
                                        ) &&
                                            isWarmUp.current) ||
                                            (!checkTestType.includes(
                                                testType.toString()
                                            ) &&
                                                test?.lastPage && (
                                                    <button
                                                        className="button-primary  text_uppercase full-width_mobile"
                                                        disabled={
                                                            selectedAns == -1 ||
                                                            loading
                                                        }
                                                        onClick={() => {
                                                            nextNavigate(false);
                                                        }}
                                                    >
                                                        {test?.lastPage
                                                            ? phrases?.[
                                                            "20023539"
                                                            ]
                                                            : phrases?.[
                                                            "10976"
                                                            ]}
                                                        <span className="material-icons">
                                                            arrow_forward
                                                        </span>
                                                    </button>
                                                ))}
                                        {checkTestType.includes(
                                            testType.toString()
                                        ) &&
                                            !isWarmUp.current && (
                                                <button
                                                    className="button-primary text_uppercase full-width_mobile"
                                                    disabled={
                                                        selectedAns == -1 ||
                                                        loading
                                                    }
                                                    onClick={() => {
                                                        nextNavigate(false);
                                                    }}
                                                >
                                                    {test?.lastPage
                                                        ? phrases?.["20023539"]
                                                        : phrases?.["10976"]}
                                                    <span className="material-icons">
                                                        arrow_forward
                                                    </span>
                                                </button>
                                            )}
                                    </div>
                                    <div className="flex justify-content_end full-width_mobile">
                                        {isWarmUp.current && test.lastPage && (
                                            <button
                                                className="button-primary text_uppercase full-width_mobile"
                                                disabled={
                                                    selectedAns == -1 || loading
                                                }
                                                onClick={handleExampleStart}
                                            >
                                                {phrases?.["1000252"]}
                                                <span className="material-icons">
                                                    arrow_forward
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Question;
